/**
 * E49 · Motivation on the portal — the client page's Motivation section (client page ▸ Skills & exercises ▸ Motivation).
 * The field lists the dynamic GraphQL client sends, the types of what comes back, and the pure bits the screen shares
 * (the order of the changes, the rulers table, the four boxes, the URICA reading, the two payload builders).
 *
 * DISPLAY ONLY for the read model: the client's own words, no verdict, no advice. The portal does not bundle `@cc/domain`, so the
 * vocabulary (the pre-work kinds, the box order) is copied here as ids only — their words are `mi.*`, never data.
 */

// ── the vocabulary (ids only) ───────────────────────────────────────────────────────────────────────

/** The pre-work a therapist can ask for before a session — the homework target type each one is. */
export const MI_ASSIGN_WHATS = [
  'changeRating',
  'decisionalBalance',
  'changePlan',
] as const;
export type MiAssignWhat = (typeof MI_ASSIGN_WHATS)[number];

/** The four boxes in the founder's order (Q-rev-6 A): staying · good, staying · not so good, changing · not so good, changing · good. */
export const BALANCE_BOXES = [
  'prosOfStaying',
  'consOfStaying',
  'consOfChange',
  'prosOfChange',
] as const;
export type BalanceBox = (typeof BALANCE_BOXES)[number];

/** A change may be open or closed; the open ones come first. */
export const STATUS_OPEN = 'open';

// ── the fields the dynamic client sends ─────────────────────────────────────────────────────────────

const URICA_FIELDS = ['type', 'score', 'readiness', 'stage', 'takenAt'];

export const CHANGE_GOALS_FIELDS = [
  'subject',
  {
    changes: [
      'goalId',
      'subject',
      'label',
      'linkedValues',
      'status',
      'outcome',
      'closingNote',
      'closedAt',
      'setBy',
      'setAt',
      {
        rulersOverTime: [
          'occurredAt',
          'importance',
          'confidence',
          'readiness',
          'focusedRuler',
        ],
      },
      'myReasons',
      'changeTalk',
      { balance: [...BALANCE_BOXES, 'writtenAt'] },
      {
        plan: [
          'changes',
          'reasons',
          { planSteps: ['step', 'when'] },
          'helpers',
          { obstacles: ['obstacle', 'response'] },
          'signsOfSuccess',
          'writtenAt',
        ],
      },
      { journal: ['noteId', 'noticedAt', 'kind', 'talk'] },
      'lastActivityAt',
      { urica: URICA_FIELDS },
    ],
  },
  { urica: URICA_FIELDS },
];

export const COMMAND_FIELDS = ['id', 'recorded'];

/** The client's values, for the "what it matters for" chips (E36). */
export const VALUES_FIELDS = [
  'subject',
  { values: ['value', 'rank', 'cardText'] },
];

// ── the shapes that come back ───────────────────────────────────────────────────────────────────────

export type Urica = {
  type: number;
  score: number;
  readiness: number;
  stage: string;
  takenAt: string;
};
export type RulersAt = {
  occurredAt: string;
  importance: number;
  confidence: number;
  readiness: number;
  focusedRuler: string | null;
};
export type Balance = Record<BalanceBox, string[]> & { writtenAt: string };
export type Plan = {
  changes: string;
  reasons: string[];
  planSteps: Array<{ step: string; when: string | null }>;
  helpers: string[];
  obstacles: Array<{ obstacle: string; response: string | null }>;
  signsOfSuccess: string | null;
  writtenAt: string;
};
export type JournalLine = {
  noteId: string;
  noticedAt: string;
  kind: string | null;
  talk: string;
};
export type Change = {
  goalId: string;
  subject: string;
  label: string;
  linkedValues: string[];
  status: string;
  outcome: string | null;
  closingNote: string | null;
  closedAt: string | null;
  setBy: string | null;
  setAt: string;
  rulersOverTime: RulersAt[];
  myReasons: string[];
  changeTalk: string[];
  balance: Balance | null;
  plan: Plan | null;
  journal: JournalLine[];
  lastActivityAt: string;
  urica: Urica | null;
};
export type ChangeGoals = {
  subject: string;
  changes: Change[];
  urica: Urica | null;
};
export type ValuesRow = {
  subject: string;
  values: Array<{
    value: string;
    rank: number | null;
    cardText: string | null;
  }>;
};

// ── what the screen shows ───────────────────────────────────────────────────────────────────────────

/** Open changes first (the most recently active first within each), the closed ones after them. */
export const orderedChanges = (changes: readonly Change[]): Change[] =>
  [...changes].sort((a, b) => {
    const open = (c: Change) => (c.status === STATUS_OPEN ? 0 : 1);
    return (
      open(a) - open(b) || b.lastActivityAt.localeCompare(a.lastActivityAt)
    );
  });

/** The rulers over time, oldest first (the order a trend reads in). */
export const rulerRows = (rows: readonly RulersAt[]): RulersAt[] =>
  [...rows].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

/** The four boxes in the founder's order, each with its lines (an unwritten box is an empty list). */
export const balanceBoxes = (
  balance: Balance | null,
): Array<{ box: BalanceBox; lines: string[] }> =>
  BALANCE_BOXES.map((box) => ({ box, lines: balance?.[box] ?? [] }));

/** Was the change written down with the client in session (by someone other than the client)? */
export const writtenInSession = (
  change: Pick<Change, 'setBy' | 'subject'>,
): boolean => !!change.setBy && change.setBy !== change.subject;

/** The URICA readiness as the person reads it ("10.4"); the score on record is ×10. */
export const readinessText = (u: Pick<Urica, 'readiness'>): string =>
  u.readiness.toFixed(1);

/** The one URICA reading to show on the page: the person-wide one, else the freshest any change carries. */
export const uricaOf = (
  goals: Pick<ChangeGoals, 'urica' | 'changes'> | null | undefined,
): Urica | null => {
  if (!goals) return null;
  if (goals.urica) return goals.urica;
  const carried = goals.changes
    .map((c) => c.urica)
    .filter((u): u is Urica => !!u);
  return carried.sort((a, b) => b.takenAt.localeCompare(a.takenAt))[0] ?? null;
};

/** The chips the therapist can pick as "what it matters for": the client's sorted values, by rank, by the name the profile shows. */
export const valueChoices = (
  profile: ValuesRow | null | undefined,
  nameOf: (v: ValuesRow['values'][number]) => string,
): string[] =>
  [...(profile?.values ?? [])]
    .sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999))
    .map(nameOf)
    .filter((n) => n.trim().length > 0);

// ── the two commands ────────────────────────────────────────────────────────────────────────────────

/** SetChangeGoal with the client — `subject` is the client; the server names the therapist as `performer`. */
export const setGoalInput = (
  clientId: string,
  label: string,
  linkedValues: readonly string[],
) => ({
  subject: clientId,
  label: label.trim(),
  linkedValues: linkedValues.length > 0 ? [...linkedValues] : null,
});

export const goalLabelProblem = (label: string): boolean =>
  label.trim().length === 0;

/** The homework target type's default `ref`: the chosen change, or none ("any change"). */
export const ANY_GOAL = '__any__';

/** "Before the next session", 09:00 on the picked calendar day, on the therapist's own clock. `day` is `yyyy-MM-dd`. */
export const dueInstant = (day: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 9, 0, 0, 0);
};

/** AssignHomework — kind tool, the target type being the pre-work, `ref` the change (omitted for "any change"). */
export const assignInputOf = (a: {
  clientId: string;
  what: MiAssignWhat;
  goalId: string;
  title: string;
  instructions: string;
  due: Date | null;
}) => ({
  subject: a.clientId,
  kind: 'tool',
  target:
    a.goalId === ANY_GOAL ? { type: a.what } : { type: a.what, ref: a.goalId },
  title: a.title.trim(),
  instructions: a.instructions.trim() || null,
  reminderPolicy: 'default',
  activityTarget: null,
  paperAllowed: false,
  dueAt: a.due ? a.due.toISOString() : null,
  schedule: null,
});
